import { UI_PRIMITIVE__slider } from '@/components/ui/slider/UI_PRIMITIVE__slider';
import { UI_PRIMITIVE__tooltip } from '@/components/ui/UI_PRIMITIVE__tooltip';
import { ENUM__component_type } from '@/enums/ENUM__component_type';
import { use_effect } from '@/hooks/_base/use_effect';
import { HOOK__notifier } from '@/hooks/HOOK__notifier';
import { use_slice_generation, use_slice_ui, use_slice_workspace } from '@/hooks/store/use_slices';
import i18n_page_generation from '@/locales/keys/i18n_page_generation.json';
import { PLACEHOLDER_bar_line } from '@/pages/_base/views/PLACEHOLDER_bar_line';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { UTILS__graph_data } from '@/utils/UTILS__graph_data';
import { assert_array_has_1_item, assert_array_not_empty } from '@shared/asserts/utils_asserts';
import { CONSTANTS__generation_settings } from '@shared/constants/CONSTANTS__generation_settings';
import { ENUM__workspace_entity } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { FIELD_PRIMITIVE__seed } from '@shared/dto/generation/graph_invoke/_fields/_primitives/FIELD_PRIMITIVE__seed';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/*
 * ALIASES
 */

const { SEED_GRID2D_DIMENSION_AMOUNT_RANGE } = CONSTANTS__generation_settings;

/*
 * COMPONENT
 */

interface PROPS {
  field: FIELD_PRIMITIVE__seed;
}
export function G_SLIDER__seed({ field }: PROPS) {
  /*
   * STORE
   */
  const { t } = useTranslation(ENUM__i18n_namespace.PAGE_GENERATION);

  const { current_workspace_user } = use_slice_workspace(['current_workspace_user']);
  const { generation_current } = use_slice_generation(['generation_current']);
  const { global__is_shift_pressed, g_general__ui_disabled } = use_slice_ui(['global__is_shift_pressed', 'g_general__ui_disabled']);

  /*
   * STATE
   */
  const component_id = `${ENUM__component_type.PAGE_GENERATION_SEED_VIEW_BLOCK}_${generation_current._id}`;
  const notifier = HOOK__notifier(component_id, ENUM__workspace_entity.ENTITY_GENERATION);

  const [value_slider, set_value_slider] = useState(field.get_value_range().length);

  const tooltip_title = t(i18n_page_generation.sidebar_settings.setting.seed.slider.tooltip.title);
  /*
   * EFFECTS
   */

  use_effect(() => set_value_slider(field.get_value_range().length), [field]);

  /*
   * EVENTS
   */
  function onValueChange(values: number[]) {
    let value_new = assert_array_has_1_item(values);

    if (value_new == value_slider) return;

    set_value_slider(value_new);
  }

  async function onValueCommit(values: number[]) {
    let seeds_amount__target = assert_array_has_1_item(values);

    set_value_slider(seeds_amount__target);

    let seeds_all = [...field.get_value_range()];
    assert_array_not_empty(seeds_all);

    // Add new seeds until we reach the target value
    const seeds_amount__current = field.get_value_range().length;

    const seeds_to_add_amount = Math.max(seeds_amount__target - seeds_amount__current, 0);

    /*
     * REMOVE
     */
    if (seeds_amount__target < seeds_amount__current) {
      seeds_all = seeds_all.slice(0, seeds_amount__target);
    }

    /*
     * ADD
     */
    for (let i = 0; i < seeds_to_add_amount; i++) {
      const seed_last = seeds_all[seeds_all.length - 1];

      const seed_new = global__is_shift_pressed
        ? FIELD_PRIMITIVE__seed.seed_generate__sequential(seeds_all, seed_last)
        : FIELD_PRIMITIVE__seed.seed_generate__random_unique(seeds_all);

      seeds_all.push(seed_new);
    }

    let field_new = field.clone();
    field_new.set_value_range(seeds_all);

    assert_array_not_empty(seeds_all);

    if (!seeds_all.includes(field_new.get_value())) {
      let value = seeds_all[0];
      field_new.set_value(value);
    }

    await UTILS__graph_data.update_field(generation_current.tool.graph, field_new);

    await notifier.send_action();
  }

  /*
   * METHODS
   */

  function is_disabled() {
    return g_general__ui_disabled;
  }

  /*
   * HTML
   */

  if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_GENERATION_UPDATE)) return <PLACEHOLDER_bar_line />;

  return (
    <UI_PRIMITIVE__tooltip {...{ tooltip_title }}>
      <div className={`max-h-10 min-h-10 flex-center px-[36px]`}>
        <UI_PRIMITIVE__slider
          disabled={is_disabled()}
          min={SEED_GRID2D_DIMENSION_AMOUNT_RANGE.min}
          max={Math.max(SEED_GRID2D_DIMENSION_AMOUNT_RANGE.max, field.get_value_range().length)}
          step={1}
          {...{ onValueChange, onValueCommit }}
          value={[value_slider]}
          defaultValue={[value_slider]}
        />
      </div>
    </UI_PRIMITIVE__tooltip>
  );
}
